import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/omniread/omniread_logo.webp';

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email) {
            toast.error('Please enter your email');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { email });
            setSubmitted(true);
            toast.success('Password reset link sent to your email');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send reset link. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-container flex flex-col items-center justify-center min-h-screen p-4 md:p-12 overflow-x-hidden">
            <div className="fade-in max-w-[450px] w-full">
                {/* Logo */}
                <div className="mb-8 text-center cursor-pointer" onClick={() => navigate('/')}>
                    <img
                        src={LOGO_URL}
                        alt="OmniRead.AI"
                        className="w-24 h-24 md:w-32 md:h-32 mx-auto block drop-shadow-xl"
                    />
                </div>

                <h1 className="gold-text mb-2 text-3xl md:text-5xl font-bold tracking-tighter text-center uppercase">
                    Forgot Password?
                </h1>

                <p className="text-sm md:text-base text-gray-400 mb-8 text-center tracking-wide">
                    {submitted
                        ? 'Check your email for reset instructions'
                        : 'Enter your email to receive a password reset link'}
                </p>

                <div className="mystical-card">
                    {submitted ? (
                        <div className="text-center">
                            <div className="text-5xl mb-4 drop-shadow-[0_0_10px_rgba(212,175,55,0.3)]">
                                📧
                            </div>
                            <p className="text-[#c9c9c9] mb-6 leading-relaxed text-base">
                                We've sent a password reset link to <strong className="text-[#d4af37]">{email}</strong>.
                                Please check your inbox and follow the instructions.
                            </p>
                            <Button
                                onClick={() => navigate('/login')}
                                className="btn-gold w-full"
                            >
                                <ArrowLeft size={20} className="mr-2" />
                                Back to Login
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="mb-6 text-left">
                                <Label htmlFor="email" className="block mb-2">
                                    Email
                                </Label>
                                <div className="relative">
                                    <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter your email"
                                        className="pl-12 bg-black/40 border-gold/30 text-base"
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="btn-gold w-full mb-4"
                                disabled={loading}
                            >
                                <Send size={20} className="mr-2" />
                                {loading ? 'Sending...' : 'Send Reset Link'}
                            </Button>

                            <div className="text-center">
                                <Link
                                    to="/login"
                                    className="text-gold no-underline text-sm flex items-center justify-center gap-2 hover:text-[#f4d03f] transition-colors font-bold"
                                >
                                    <ArrowLeft size={16} />
                                    Back to Login
                                </Link>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
